/**
 * Presentation mode (key 3): a clean full-screen view for selling at the kitchen table on a tablet. The house in 3D
 * or 2D with its panels, the headline numbers, the savings chart and the payment options, with big touch targets
 * and no editing chrome. Turning a roof face's panels off or on changes the design (as an unsaved edit) and every
 * number follows live.
 */
import { Box, Map as MapIcon, X } from "lucide-react";
import type { S } from "../api/client";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { money, num } from "../lib/format";
import { CashFlowChart } from "./CashFlowChart";
import { monthlyLine, PAYMENT_LABEL_KEY, paybackText, type PaymentType } from "./payments.ts";
import "./plan.css";

export interface FaceToggle {
  id: string;
  label: string;
  panels: number;
  on: boolean;
}

export interface PresentationProps {
  title: string;
  numbers: S["EditorNumbers"];
  pricing: boolean;
  savings: S["SavingsResult"] | null;
  savingsLoading: boolean;
  payment: PaymentType;
  onPayment: (t: PaymentType) => void;
  faces: FaceToggle[];
  onFace: (id: string, on: boolean) => void;
  view: "2d" | "3d";
  onView: (v: "2d" | "3d") => void;
  onExit: () => void;
}

export function PresentationOverlay(p: PresentationProps) {
  const t = useT();
  const intl = useIntlShape();
  const currency = p.savings?.currency ?? p.numbers.currency ?? "USD";
  const option = p.savings?.options.find((o) => o.type === p.payment) ?? p.savings?.options[0] ?? null;
  const line = option ? monthlyLine(option) : null;
  const years = (p.savings?.assumptions.find((a) => a.key === "analysis_years")?.value as number | undefined) ?? 25;
  const busy = p.pricing || p.savingsLoading;
  return (
    <>
      <div className="present-views" role="tablist" aria-label={t("editor.presentation.views")}>
        <button className={p.view === "3d" ? "on" : ""} onClick={() => p.onView("3d")} role="tab" aria-selected={p.view === "3d"}>
          <Box size={22} /> {t("editor.view.threeD")}
        </button>
        <button className={p.view === "2d" ? "on" : ""} onClick={() => p.onView("2d")} role="tab" aria-selected={p.view === "2d"}>
          <MapIcon size={22} /> {t("editor.presentation.map")}
        </button>
      </div>
      <button className="present-close" onClick={p.onExit} aria-label={t("editor.presentation.exit")}>
        <X size={24} />
      </button>
      <aside className={`present-panel${busy ? " busy" : ""}`} aria-live="polite">
        <header>
          <small>{t("editor.presentation.kicker")}</small>
          <h2 dir="auto">{p.title}</h2>
        </header>
        <div className="present-kpis">
          <Headline label={t("editor.presentation.system")} value={`${num(p.numbers.kw_dc, 1)} kW`} sub={t("editor.panels.count", { count: p.numbers.panel_count })} />
          <Headline label={t("editor.presentation.energy")} value={`${num(p.numbers.annual_kwh)} kWh`} sub={p.numbers.offset_pct != null ? t("editor.presentation.offset", { percent: intl.formatNumber(p.numbers.offset_pct / 100, { style: "percent", maximumFractionDigits: 0 }) }) : undefined} />
          <Headline label={t("editor.presentation.savingsYear1")} value={p.savings ? money(p.savings.year1_savings.mid, currency) : money(p.numbers.annual_savings, currency)} sub={p.savings ? t("editor.presentation.perMonth", { amount: money(p.savings.monthly_savings_year1, currency) }) : undefined} accent />
          <Headline label={t("editor.presentation.payback")} value={option ? paybackText(t, option.payback_years, years) : p.numbers.payback_years != null ? paybackText(t, p.numbers.payback_years, years) : "—"} />
        </div>
        {p.savings && p.savings.options.length > 0 && (
          <div className="present-pay" role="tablist" aria-label={t("editor.presentation.paymentOption")}>
            {p.savings.options.map((o) => (
              <button key={o.type} className={o.type === option?.type ? "on" : ""} onClick={() => p.onPayment(o.type)} role="tab" aria-selected={o.type === option?.type}>
                {t(PAYMENT_LABEL_KEY[o.type])}
              </button>
            ))}
          </div>
        )}
        {line && option && (
          <div className="present-monthly">
            <div>
              <span>{option.type === "cash" ? t("editor.presentation.upFront") : t("editor.presentation.monthlyPayment")}</span>
              <b>{option.type === "cash" ? money(line.upfront, currency) : money(line.payment, currency)}</b>
            </div>
            <div>
              <span>{t("editor.presentation.monthlySavings")}</span>
              <b className="ok-text">{money(line.savings, currency)}</b>
            </div>
            <div>
              <span>{option.type === "cash" ? t("editor.presentation.lifetimeSavings") : t("editor.presentation.betterOff")}</span>
              <b className={option.type === "cash" || line.net >= 0 ? "ok-text" : "warn-text"}>{option.type === "cash" ? money(option.lifetime_savings, currency) : money(line.net, currency)}</b>
            </div>
          </div>
        )}
        {option && <CashFlowChart flows={option.cash_flows} currency={currency} width={560} height={170} large />}
        {p.faces.length > 0 && (
          <div className="present-faces">
            <span className="present-label">{t("editor.presentation.roofFaces")}</span>
            {p.faces.map((f) => (
              <button key={f.id} className={`present-face${f.on ? " on" : ""}`} onClick={() => p.onFace(f.id, !f.on)} aria-pressed={f.on}>
                <span className="pf-switch" aria-hidden />
                <span>{t("editor.panels.face", { label: f.label })}</span>
                <small>{f.on ? t("editor.panels.count", { count: f.panels }) : t("editor.presentation.noPanels")}</small>
              </button>
            ))}
          </div>
        )}
      </aside>
    </>
  );
}

function Headline({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className={`present-kpi${accent ? " accent" : ""}`}>
      <span>{label}</span>
      <b dir="auto">{value}</b>
      {sub && <small>{sub}</small>}
    </div>
  );
}
